<script setup lang="ts">
import type { MultifactorAuthenticationOptions } from './ManageMyProfile';
import type {
	CurrentUserProfileResponse,
	UpdateCurrentUserProfileRequest
} from '@maple-tech/aspire-core-api-types/v1/user';
import { ref, computed } from 'vue';
import InputText from 'primevue/inputtext';
import InputMask from 'primevue/inputmask';
import Button from 'primevue/button';
import Dropdown from 'primevue/dropdown';
import Checkbox from 'primevue/checkbox';
import Message from 'primevue/message';
import Card from 'primevue/card';
import {
	useForm,
	useIsFormDirty,
	useIsFormValid,
	useFormErrors,
	useValidateForm
} from 'vee-validate';
import { baseUrl, fetchWrapper } from '@/helpers/fetchWrapper';
import * as yup from '@/helpers/yup-distilled';

const getCurrentUserProfile = async () => {
	/* 2025-01-23 aspire-core-api will not look for a cookie if there is a token, even expired */

	try {
		const currentUserProfileResp = await fetch(`${baseUrl}/users/current`, {
			credentials: 'include'
		});

		return (await currentUserProfileResp.json()) as
			| CurrentUserProfileResponse
			| undefined;
	} catch (e) {
		alert('User fetch has failed.');
	}

	/* END PATCH */
};

const initialValuesFromUserProfile = (
	userProfile: CurrentUserProfileResponse | undefined
) => {
	return {
		...userProfile,
		phoneNumber: phoneFormatFlat(userProfile?.phoneNumber || ''),
		useSMSForMFA:
			multifactorAuthenticationCodes[
				userProfile?.multifactorAuthenticationID || NaN
			]?.sms || false,
		useEmailForMFA:
			multifactorAuthenticationCodes[
				userProfile?.multifactorAuthenticationID || NaN
			]?.email || false
	};
};

const currentUserProfile = await getCurrentUserProfile();

const formattedPhoneRegex = new RegExp(/\(\d\d\d\) \d\d\d-\d\d\d\d/);
const phoneFlatten = (formattedPhone: string) =>
	formattedPhone.replace(/\D/g, '');
const phoneFormatFlat = (unformattedPhone: string) =>
	unformattedPhone.length !== 10
		? unformattedPhone
		: `(${unformattedPhone.substring(0, 3)}) ${unformattedPhone.substring(3, 6)}` +
			`-${unformattedPhone.substring(6, 10)}`;

const emailRegex = new RegExp(
	/(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/
);

const multifactorAuthenticationCodes =
	new Array<MultifactorAuthenticationOptions>();
multifactorAuthenticationCodes[1] = { sms: true, email: false };
multifactorAuthenticationCodes[2] = { sms: false, email: true };
multifactorAuthenticationCodes[3] = { sms: true, email: true };
//  TODO - potentially get this from an API

const schema = yup.object().shape({
	firstName: yup.string().required().min(1).max(100),
	mi: yup.string().optional(),
	lastName: yup.string().required().min(1).max(100),
	email: yup
		.string()
		.required()
		.test('Email', 'Please enter a valid email.', (email) =>
			emailRegex.test(email)
		)
		.min(1)
		.max(500),
	phoneNumber: yup
		.string()
		.test((phone) => !phone || formattedPhoneRegex.test(phone)),
	isMobilePhone: yup.boolean(),
	useEmailForMFA: yup
		.boolean()
		.required()
		.test(
			'emailAddressForEmail',
			'Email address required in order to receive codes by email.',
			(val, context) => !val || context.parent.email !== ''
		),
	useSMSForMFA: yup
		.boolean()
		.required()
		.test(
			'mobilePhoneForSMS',
			'To receive codes by text, please be sure that a phone number is entered, and is confirmed to be a mobile number.',
			(val, context) =>
				!val ||
				(context.parent.phoneNumber !== '' &&
					context.parent.isMobilePhone)
		)
});

const { defineField, errors, handleSubmit, isSubmitting } = useForm({
	validationSchema: schema,
	initialValues: initialValuesFromUserProfile(currentUserProfile)
});
const [login] = defineField('login');
const [firstName] = defineField('firstName');
const [mi] = defineField('mi');
const [lastName] = defineField('lastName');
const [email] = defineField('email');
const [phoneNumber] = defineField('phoneNumber');
const [isMobilePhone] = defineField('isMobilePhone');
const [useSMSForMFA] = defineField('useSMSForMFA');
const [useEmailForMFA] = defineField('useEmailForMFA');
const apiError = ref<string>('');
const successMessage = ref<string>('');

const phoneNumberUnformatted = computed(() => phoneFlatten(phoneNumber.value));

const multifactorAuthenticationID = computed(() => {
	const index = multifactorAuthenticationCodes.findIndex(
		(value) =>
			value &&
			value.sms == useSMSForMFA.value &&
			value.email == useEmailForMFA.value
	);
	return index === -1 ? null : index;
});

const save = handleSubmit(async (values, { setErrors, resetForm }) => {
	const { firstName, mi, lastName, email, phoneNumber, isMobilePhone } =
		values;

	const initials = `${firstName?.substring(0, 1)}${mi}${lastName?.substring(0, 1)}`;
	const phoneCountryCallingCode = '1'; // Pending expansion to non-US
	if (!multifactorAuthenticationID.value) {
		setErrors({
			useEmailForMFA: 'Multifactor authentication must be specified.'
		});
		return;
	}
	if (!firstName || !lastName || !email) {
		return;
	}

	const updateUserProfileRequest: UpdateCurrentUserProfileRequest = {
		firstName: firstName || '',
		mi: mi || '',
		lastName,
		email,
		initials,
		phoneCountryCallingCode,
		phoneNumber: phoneNumberUnformatted.value,
		isMobilePhone: isMobilePhone || false,
		multifactorAuthenticationID: multifactorAuthenticationID.value
	};

	try {
		await fetch(`${baseUrl}/users/current`, {
			credentials: 'include',
			body: JSON.stringify(updateUserProfileRequest),
			method: 'PUT',
			headers: {
				'Content-Type': 'application/json'
			}
		});

		const userProfile = await getCurrentUserProfile();
		resetForm({ values: initialValuesFromUserProfile(userProfile) });
		apiError.value = '';
		successMessage.value = 'The user update has succeeded.';
	} catch (err) {
		apiError.value = 'The user update failed.';
	}
});

const isFormDirty = useIsFormDirty();
const isFormValid = useIsFormValid();
const validateForm = useValidateForm();
await validateForm();
</script>
<template>
	<div v-if="currentUserProfile" class="outer-container">
		<form @submit="save">
			<h2>Manage Your Profile</h2>
			<h3>Basic Information</h3>
			<div class="formgroup-inline">
				<div class="field">
					<label for="login">Login</label>
					<InputText v-model="login" disabled id="login"></InputText>
				</div>
				<div class="field">
					<label for="firstName">First Name</label>
					<InputText
						v-model="firstName"
						disabled
						id="firstName"
					></InputText>
				</div>
				<div class="field">
					<label for="mi">MI</label>
					<InputText
						v-model="mi"
						disabled
						id="mi"
						class="w-2rem"
					></InputText>
				</div>
				<div class="field">
					<label for="lastName">Last Name</label>
					<InputText
						v-model="lastName"
						disabled
						id="lastName"
					></InputText>
				</div>
			</div>
			<div class="field flex flex-column">
				<label for="email">Email</label>
				<InputText
					v-model="email"
					id="email"
					class="w-30rem"
					:invalid="!!errors.email"
				></InputText>
				<small>{{ errors.email }}</small>
			</div>
			<div class="field flex flex-row">
				<div class="flex flex-column">
					<label for="phoneNumber">Phone Number</label>
					<InputMask
						v-model="phoneNumber"
						mask="(999) 999-9999"
						id="phoneNumber"
						class="w-10rem"
						:invalid="!!errors.phoneNumber"
					/>
					<small id="phoneNumber-help"
						>(10-digit phone numbers only)</small
					>
					<small>{{ errors.phoneNumber }}</small>
				</div>
				<div class="ml-4 flex flex-column">
					<label for="isMobilePhone"
						>Is this a mobile phone number?</label
					>
					<Dropdown
						v-model="isMobilePhone"
						:options="[
							{ name: 'Yes', value: true },
							{ name: 'No', value: false }
						]"
						optionLabel="name"
						optionValue="value"
						id="isMobilePhone"
					></Dropdown>
				</div>
			</div>
			<h3>Multifactor Authentication</h3>
			<div class="mb-4">
				How would you like to be able to receive one-time codes to sign
				into Aspire?
			</div>
			<div class="field-checkbox">
				<Checkbox
					v-model="useSMSForMFA"
					:invalid="!!errors.useSMSForMFA"
					:binary="true"
				></Checkbox>
				<label for="useSMSForMFA">Receive codes by text message?</label>
			</div>
			<Message
				v-if="!!errors.useSMSForMFA"
				severity="error"
				:closable="false"
				>{{ errors.useSMSForMFA }}</Message
			>
			<div class="field-checkbox">
				<Checkbox
					v-model="useEmailForMFA"
					:invalid="!!errors.useEmailForMFA"
					:binary="true"
				></Checkbox>
				<label for="useEmailForMFA">Receive codes by email?</label>
			</div>
			<Message
				v-if="!!errors.useEmailForMFA"
				severity="error"
				:closable="false"
				>{{ errors.useEmailForMFA }}</Message
			>
			<input
				id="multifactorAuthenticationID"
				v-model="multifactorAuthenticationID"
				type="hidden"
			/>
			<Button
				type="submit"
				:disabled="isSubmitting || !isFormDirty || !isFormValid"
				>Save</Button
			>
			<Message severity="error" v-if="apiError !== ''" :life="3000">{{
				apiError
			}}</Message>
			<Message
				severity="success"
				v-if="successMessage !== '' && !isFormDirty"
				>{{ successMessage }}</Message
			>
		</form>
	</div>
</template>

<style scoped>
.outer-container {
	padding: 2rem;
}
</style>
